import React from "react";

export type PriorityBand = "critical" | "high" | "elevated" | "low" | "none";

type Props = {
  /** 0-100 priority score. Drives sorting elsewhere; shown here only as a band
   *  word. Null/undefined renders a muted dash. */
  score?: number | null;
  /** Band from trendUtils. If omitted, derived from the score. */
  band?: string | null;
  className?: string;
};

const bandStyle: Record<PriorityBand, { chip: string; label: string }> = {
  critical: { chip: "bg-alert-critical/15 text-alert-critical", label: "Critical" },
  high: { chip: "bg-alert-fire/15 text-alert-fire", label: "High" },
  elevated: { chip: "bg-alert-warning/20 text-alert-warning", label: "Elevated" },
  low: { chip: "bg-black/5 text-ink-2", label: "Low" },
  none: { chip: "", label: "None" },
};

function deriveBand(score: number): PriorityBand {
  if (score >= 70) return "critical";
  if (score >= 45) return "high";
  if (score >= 20) return "elevated";
  if (score >= 1) return "low";
  return "none";
}

/**
 * "Most problematic" priority shown as a band word (Critical down to muted None)
 * on a tinted chip. The underlying 0-100 score still drives table sorting and
 * feed ordering but isn't surfaced, since it has no standalone meaning to users.
 * Healthy (None) and unknown values render a muted dash to keep tables calm.
 */
export default function PriorityBadge({ score, band, className = "" }: Props) {
  const key =
    score == null
      ? null
      : (band as PriorityBand) in bandStyle
        ? (band as PriorityBand)
        : deriveBand(score);

  if (key == null || key === "none") {
    return (
      <span className="text-ink-3" title="Priority: None" aria-label="No priority">
        -
      </span>
    );
  }

  const { chip, label } = bandStyle[key];
  return (
    <span
      className={`inline-flex items-center rounded-chip px-2.5 py-1 text-xs font-bold ${chip} ${className}`}
      title={`Priority: ${label}`}
    >
      {label}
    </span>
  );
}
